import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { MapPin, Clock, Star, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import heroToursImg from "@/assets/hero-tours.jpg";

const ToursPage = () => {
  usePageSeo("tours");
  const { data: tours = [], isLoading } = useQuery({
    queryKey: ["tours-page"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*, destinations(title)")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img src={heroToursImg} alt="Tours in Dominican Republic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/75" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <p className="text-primary font-semibold tracking-wide uppercase text-sm mb-3">Explore Dominican Republic</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-foreground mb-6">
              Unforgettable Tours & Excursions
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mb-8">
              Discover the beauty of the Caribbean with our curated selection of tours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">All Tours</h2>
              <p className="text-muted-foreground">Handpicked experiences for every traveler</p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.map((tour: any, index: number) => (
                <motion.div
                  key={tour.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative bg-card rounded-lg overflow-hidden border border-border shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <Link to={`/tours/${tour.slug}`}>
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={tour.main_image_url || "/placeholder.svg"} 
                        alt={tour.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      
                      {tour.is_featured && (
                        <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-md">
                          <Star className="w-3 h-3 inline mr-1" />Featured
                        </div>
                      )}
                      
                      {tour.destinations?.title && (
                        <div className="absolute bottom-4 left-4 flex items-center gap-1 text-white">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm font-medium">{tour.destinations.title}</span>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h3 className="font-display text-xl font-bold text-card-foreground mb-2 group-hover:text-primary transition-colors">
                        {tour.title}
                      </h3>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {tour.duration || "Full Day"}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-muted-foreground text-sm">From</span>
                          <p className="text-2xl font-bold text-primary">${tour.adult_price || 0}</p>
                        </div>
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg">
                          Book Now
                        </Button>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-6">
              Ready for Your Caribbean Adventure?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">Contact us for custom tour packages and group bookings.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact"><Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-lg px-8">Contact Us</Button></Link>
              <Link to="/transfers"><Button size="lg" variant="outline" className="border-white/40 text-primary-foreground hover:bg-white/10 rounded-lg px-8 text-gray-950">Book a Transfer</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ToursPage;
